import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@material-ui/core/Box";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import { AuthErrorSummary, AuthNote, AuthStatusMark } from "@/components/AuthFeedback";
import { CustomButton } from "@/components/Button";
import { CustomForm } from "@/components/Form";
import { useResetPassword } from "@/hooks/useResetPassword";
import { EntranceActions, EntranceTemplate } from "@/layouts/EntranceTemplate";
import { passwordResetSchema, PasswordResetFormData } from "@/validations/passwordReset";

export const PasswordResetPage: React.FC = () => {
  const location = useLocation();
  // トークンが変わった場合、前のリンクの成功・使用不可状態を持ち越さない。
  const token = new URLSearchParams(location.search).get("prt");
  return <PasswordResetForm key={token ?? "missing"} token={token} />;
};

const PasswordResetForm: React.FC<{ token: string | null }> = ({ token }) => {
  const { resetPassword, loading, error, complete, invalidToken } = useResetPassword(token);
  const history = useHistory();
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
    mode: "onTouched",
    shouldFocusError: true,
    defaultValues: { password: "", password_confirmation: "" },
  });
  if (!token || invalidToken)
    return (
      <EntranceTemplate
        title="このリンクは使用できません"
        subTitle="有効期限が切れているか、すでに使われています。"
        mark={<AuthStatusMark invalid />}
      >
        <AuthNote title="もう一度やりなおすには">
          再設定のリンクをもう一度送信してください。
          <br />
          リンクは送信から60分で使用できなくなります。
        </AuthNote>
        <EntranceActions>
          <CustomButton buttonSize="L" fullWidth onClick={() => history.push("/password_reset/new")}>
            リンクを送りなおす
          </CustomButton>
          <Box mt={0.5}>
            <CustomButton type="button" tone="text" onClick={() => history.push("/login")}>
              ログインに戻る
            </CustomButton>
          </Box>
        </EntranceActions>
      </EntranceTemplate>
    );
  if (complete)
    return (
      <EntranceTemplate
        title="パスワードを変更しました"
        subTitle="新しいパスワードでもう一度ログインしてください。"
        mark={<AuthStatusMark />}
      >
        <EntranceActions>
          <CustomButton buttonSize="L" fullWidth onClick={() => history.push("/login")}>
            ログインする
          </CustomButton>
        </EntranceActions>
      </EntranceTemplate>
    );
  return (
    <EntranceTemplate title="新しいパスワードを設定する" subTitle="設定後、新しいパスワードでログインしてください。">
      <form onSubmit={handleSubmit(resetPassword)} noValidate>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <Controller
            name="password"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="新しいパスワード"
                helperText="半角の英字と数字を含む8〜72文字"
                type="password"
                autoComplete="new-password"
                disabled={loading}
                error={!!errors.password}
                errorText={errors.password && <span dangerouslySetInnerHTML={{ __html: errors.password.message }} />}
              />
            )}
          />
          <Controller
            name="password_confirmation"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="もう一度入力"
                type="password"
                autoComplete="new-password"
                disabled={loading}
                error={!!errors.password_confirmation}
                errorText={errors.password_confirmation?.message}
              />
            )}
          />
        </Box>
        <EntranceActions>
          <AuthErrorSummary message={error} />
          <CustomButton type="submit" buttonSize="L" fullWidth disabled={loading}>
            パスワードを変える
          </CustomButton>
          <Box mt={0.5}>
            <CustomButton type="button" tone="text" onClick={() => history.push("/login")}>
              ログインに戻る
            </CustomButton>
          </Box>
        </EntranceActions>
      </form>
    </EntranceTemplate>
  );
};
